/**
 * Utilities for local frontend-only development mode.
 */

/** Returns true when both dev auth tokens are present in localStorage. */
export const hasDevAuthKeys = (): boolean =>
  typeof window !== 'undefined' &&
  Boolean(window.localStorage.getItem('devx_backstage_token')) &&
  Boolean(window.localStorage.getItem('devx_ms_token'));

/** Retrieves a token string from localStorage by key. Returns null outside a browser context. */
export const getLocalToken = (key: string): string | null =>
  typeof window !== 'undefined' ? window.localStorage.getItem(key) : null;

/** Decodes the payload of a JWT without verifying the signature. Returns {} on failure. */
export const decodeJwtPayload = (token: string): Record<string, any> => {
  try {
    return JSON.parse(atob(token.split('.')[1]));
  } catch {
    return {};
  }
};

/**
 * Seeds placeholder dev tokens into localStorage if not already set.
 * Developers can overwrite these with real tokens from UAT DevTools.
 */
export const seedDevTokens = (): void => {
  if (hasDevAuthKeys()) return;
  window.localStorage.setItem('devx_backstage_token', 'dev');
  window.localStorage.setItem('devx_ms_token', 'dev');
};

/** Clears dev auth tokens from localStorage. Does not touch the production token cookie. */
export const clearDevTokens = (): void => {
  window.localStorage.removeItem('devx_backstage_token');
  window.localStorage.removeItem('devx_ms_token');
};

// ---------------------------------------------------------------------------
// Token prompt service
// Lets the axios 401 interceptor (non-React code) request fresh tokens from
// the TokenRefreshDialog component without a direct dependency on React.
// ---------------------------------------------------------------------------

type ResolveCallback = () => void;

class TokenPromptService {
  private pending: ResolveCallback | null = null;
  private subscriber: (() => void) | null = null;

  /** Called by the axios interceptor on 401 in frontendOnly mode. */
  prompt(): Promise<void> {
    return new Promise<void>(resolve => {
      this.pending = resolve;
      if (this.subscriber) this.subscriber();
    });
  }

  /** Called by the dialog to subscribe; returns an unsubscribe function. */
  subscribe(callback: () => void): () => void {
    this.subscriber = callback;
    return () => {
      if (this.subscriber === callback) this.subscriber = null;
    };
  }

  /** Called by the dialog once the user has saved tokens. */
  resolve(): void {
    if (this.pending) {
      const cb = this.pending;
      this.pending = null;
      cb();
    }
  }
}

export const tokenPromptService = new TokenPromptService();
